import { useCallback, useEffect, useRef, useState } from 'react';
import { useWindowDimensions } from 'react-native';
import {
    cancelAnimation,
    interpolate,
    runOnJS,
    useAnimatedStyle,
    useSharedValue,
    withTiming,
} from 'react-native-reanimated';

const SLIDE_DURATION_MS = 220;

// Slideout drawer lifecycle for the (tabs) host.
//
// - `mounted` latches on first open: the menu tree is mounted lazily (a closed
//   drawer never starts queries/listeners) and then RETAINED across closes, so
//   reopening doesn't repeat the mount + query + layout burst that previously
//   contended with the opening animation.
// - The first slide-in is deferred to the panel's onLayout: mounting the menu
//   and starting the translate animation in the same frame is what produced
//   the open stutter. Warm opens animate immediately.
// - `open` is released only by the close animation's finished callback — an
//   interrupted close (rapid reopen) reports finished=false and must not
//   clear the state, and a completed close can't be undone by a stale timer.
const useSlideoutDrawer = () => {
    const { width: screenWidth } = useWindowDimensions();
    const [mounted, setMounted] = useState(false);
    const [open, setOpen] = useState(false);
    const lifecycle = useRef({ mounted: false, ready: false, requestedOpen: false, generation: 0, live: true });
    const drawerAnim = useSharedValue(0);

    useEffect(() => {
        lifecycle.current.live = true;
        return () => {
            lifecycle.current.live = false;
            lifecycle.current.generation += 1;
            cancelAnimation(drawerAnim);
        };
    }, [drawerAnim]);

    const openDrawer = useCallback(() => {
        const state = lifecycle.current;
        if (!state.live) return;
        state.requestedOpen = true;
        state.generation += 1;
        if (!state.mounted) {
            state.mounted = true;
            setMounted(true);
        }
        if (state.ready) {
            drawerAnim.value = withTiming(1, { duration: SLIDE_DURATION_MS });
        }
        setOpen(true);
    }, [drawerAnim]);

    const finishClose = useCallback((generation) => {
        const state = lifecycle.current;
        // UI completion can already be queued on JS when another open starts.
        if (state.live && !state.requestedOpen && state.generation === generation) {
            setOpen(false);
        }
    }, []);

    const closeDrawer = useCallback(() => {
        const state = lifecycle.current;
        if (!state.live) return;
        state.requestedOpen = false;
        const generation = ++state.generation;
        drawerAnim.value = withTiming(0, { duration: SLIDE_DURATION_MS }, (finished) => {
            if (finished) runOnJS(finishClose)(generation);
        });
    }, [drawerAnim, finishClose]);

    const onPanelLayout = useCallback(() => {
        const state = lifecycle.current;
        if (!state.live || state.ready) return;
        state.ready = true;
        if (state.requestedOpen) {
            drawerAnim.value = withTiming(1, { duration: SLIDE_DURATION_MS });
        }
    }, [drawerAnim]);

    const panelStyle = useAnimatedStyle(() => ({
        transform: [{ translateX: interpolate(drawerAnim.value, [0, 1], [-screenWidth, 0]) }],
    }));

    const backdropStyle = useAnimatedStyle(() => ({
        opacity: interpolate(drawerAnim.value, [0, 1], [0, 1]),
    }));

    return { mounted, open, openDrawer, closeDrawer, onPanelLayout, panelStyle, backdropStyle };
};

export default useSlideoutDrawer;
